import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, DropdownItem, DropdownLabel, DropdownMenu, DropdownSeparator } from '../ui';
import { useSessionStore } from '../../stores/session';
import { useWorkspaceStore } from '../../stores/workspace';
import { cn } from '../../lib/utils';

export interface WorkspaceSwitcherProps {
  className?: string;
  /** `compact` fits the topbar; `full` is the wide sidebar/onboarding block. */
  variant?: 'compact' | 'full';
  align?: 'start' | 'end';
}

/**
 * Active-workspace picker. Reads the list from the session store (populated by
 * /api/auth/me) and writes the choice through the workspace store, which is what
 * puts `X-Workspace-Id` on every subsequent request.
 */
export function WorkspaceSwitcher({
  className,
  variant = 'compact',
  align = 'start',
}: WorkspaceSwitcherProps) {
  const workspaces = useSessionStore((state) => state.workspaces);
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const navigate = useNavigate();

  const active = workspaces.find((workspace) => workspace.id === workspaceId);

  return (
    <DropdownMenu
      className={className}
      align={align}
      menuClassName="min-w-64"
      trigger={
        <button
          type="button"
          aria-label="Switch workspace"
          className={cn(
            'flex items-center gap-2 rounded-lg border border-border bg-surface text-left',
            'transition-colors hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            variant === 'full' ? 'w-full px-3 py-2' : 'h-9 max-w-56 px-2',
          )}
        >
          <Avatar
            name={active?.name ?? 'Workspace'}
            size="xs"
            shape="square"
            className="rounded-md"
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-fg">
              {active?.name ?? 'No workspace'}
            </span>
            {variant === 'full' && active && (
              <span className="block truncate text-xs capitalize text-subtle">
                {active.role} · {active.memberCount} member{active.memberCount === 1 ? '' : 's'}
              </span>
            )}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-subtle" aria-hidden />
        </button>
      }
    >
      {(close) => (
        <>
          <DropdownLabel>Workspaces</DropdownLabel>
          {workspaces.length === 0 && (
            <p className="px-2.5 py-2 text-xs text-subtle">
              You are not in a workspace yet — create one below.
            </p>
          )}
          {workspaces.map((workspace) => (
            <DropdownItem
              key={workspace.id}
              active={workspace.id === workspaceId}
              onSelect={() => {
                setWorkspace(workspace.id);
                close();
              }}
              icon={
                workspace.id === workspaceId ? (
                  <Check className="text-accent" />
                ) : (
                  <span className="size-3.5" />
                )
              }
              trailing={<span className="capitalize">{workspace.role}</span>}
            >
              {workspace.name}
            </DropdownItem>
          ))}
          <DropdownSeparator />
          <DropdownItem
            icon={<Plus />}
            onSelect={() => {
              close();
              navigate('/onboarding?new=1');
            }}
          >
            New workspace
          </DropdownItem>
        </>
      )}
    </DropdownMenu>
  );
}
